/* eslint-disable camelcase */
import _cloneDeep from 'lodash/cloneDeep'
import _isUndefined from 'lodash/isUndefined'
import { drawRect } from './drawRect'
import { drawIcon } from './drawIcon'
import {
  drawGroupSeats,
  resetColorsData,
  tierColorObj,
  colorIndex
} from './drawGroupSeats'
import { drawGroupBlock } from './drawGroupBlock'
import { drawMapWithSVG } from './drawMapWithSVG'
import { drawStandingArea } from './drawStandingArea'
import { drawTable } from './drawTable'
import { drawLabel } from './drawLabel'
import { COLORS, TIER_COLORS } from '../constants'
import { textGeometries, materials } from '../utils/mapData'
import { raycastMouseEvents } from '../Raycasting/raycastMouseEvents'

export const drawMap = async (
  seatmap,
  groups,
  creatorInfo,
  predefinedSeats,
  options
) => {
  const {
    scene,
    scale,
    fonts,
    center_x,
    center_y,
    seat_circle_radius,
    events,
    isBlockMap,
    enableTicketTypeAssign,
    selectedTierId,
    renderer,
    camera
  } = options
  seatmap = _cloneDeep(seatmap)
  const sceneMeshes = []
  resetColorsData()
  for (const group of seatmap) {
    if (group.deleted === 0) {
      switch (group.type) {
        case 'section': {
          isBlockMap
            ? drawGroupBlock(group, groups, creatorInfo, {
                sceneMeshes,
                ...options
              })
            : drawGroupSeats(group, groups, predefinedSeats, {
                sceneMeshes,
                ...options
              })
          break
        }
        case 'with_svg': {
          await drawMapWithSVG(
            group,
            groups,
            { sceneMeshes, ...options },
            predefinedSeats
          )
          break
        }
        case 'stencil': {
          drawRect(group, {
            color: group.bg_color || COLORS.STAGE,
            borderColor: group.border_color || COLORS.STAGE_BORDER,
            scale,
            scene,
            center_x,
            center_y
          })

          const iconCode =
            group.stencil === 'ring' || group.stencil === 'dance_floor'
              ? null
              : group.stencil === 'basketball'
              ? '\uF434'
              : group.stencil === 'dj'
              ? '\uF025'
              : '\uF130'
          const iconPosition = iconCode
            ? [group.x * scale, -group.y * scale, 0]
            : [0, 0]

          iconCode &&
            drawIcon(iconCode, {
              font: fonts.fontAwesome,
              color: 0x6a727a,
              position: iconPosition,
              center_x,
              center_y,
              scale,
              scene
            })

          const labelText = group.label || 'Stage'
          const labelPosition =
            !_isUndefined(group.label_x) && !_isUndefined(group.label_y)
              ? [group.label_x * scale, group.label_y * scale, 0]
              : [group.x * scale, -(group.y + 24) * scale, 0]

          drawLabel(
            labelText,
            {
              font: fonts.openSans,
              color: 'black',
              position: labelPosition,
              center_x,
              center_y,
              scale,
              seat_circle_radius,
              scene,
              size: group.label_size * (scale || 1) || 12,
              rotation: group.label_rotation || 0
            },
            textGeometries,
            materials
          )
          break
        }
        case 'standing': {
          const { tier: tierId, label } = groups[group.id]

          if (!tierColorObj[tierId]) {
            tierColorObj[tierId] = TIER_COLORS[colorIndex.value]
            colorIndex.value++
          }

          drawStandingArea(group, {
            color: COLORS.AVAILABLE,
            scale,
            scene,
            opacity: 1,
            center_x,
            center_y,
            events,
            tierId,
            label,
            materialGroup: groups[group.id],
            id: group.id,
            fonts,
            seat_circle_radius,
            enableTicketTypeAssign,
            tierColor: tierColorObj[tierId],
            selectedTierId,
            sceneMeshes
          })

          if (!group.hideSectionLabel && !group.labelPositions) {
            // TODO this piece should be refactored (there is duplicate in multiple rects part)
            const labelText = groups[group.id].label
            const height = group.height || 0 // if oval or ring
            const innerHeight = group.inner_height || 0 // if oval or ring

            let labelPosition = [group.x * scale, -group.y * scale, 0]

            if (innerHeight) {
              let y =
                -(group.y + (innerHeight + (height - innerHeight) / 2) / 2) *
                scale

              if (group.rect_type === 'top_rect') {
                y = -y
              }

              labelPosition = [group.x * scale, y, 0]
            }

            if (group.rotation === 90 || group.rotation === -90) {
              labelPosition = [
                (group.y * scale * group.rotation) / 90,
                (group.x * scale * group.rotation) / 90,
                0
              ]
            }

            const labelX = group.label_x || 0
            const labelY = group.label_y || 0
            labelPosition = [
              labelPosition[0] + labelX * scale,
              labelPosition[1] + labelY * scale,
              0
            ]

            drawLabel(
              labelText,
              {
                font: fonts.openSans,
                color: 'black',
                position: labelPosition,
                center_x,
                center_y,
                scale,
                seat_circle_radius,
                scene,
                opacity: 0.9,
                size: group.size || 12,
                rotation: group.label_rotation
              },
              textGeometries,
              materials
            )
          }
          break
        }
        case 'general_admission_section': {
          let rectColor, iconCode, iconColor
          if (group.seatingType === 'seated') {
            rectColor = '#EC00BE'
            iconColor = '#f8f8fa'
            iconCode = String.fromCharCode(0xf6c0)
          } else {
            rectColor = '#56D36A'
            iconColor = '#6a727a'
            iconCode = String.fromCharCode(0xf183)
          }
          drawRect(group, {
            color: rectColor,
            scale,
            scene,
            center_x,
            center_y
          })

          const iconPosition = [group.x * scale, -group.y * scale, 0]
          drawIcon(iconCode, {
            font: fonts.fontAwesome,
            color: iconColor,
            position: iconPosition,
            center_x,
            center_y,
            scale,
            scene
          })

          const labelText = groups[group.id].label
          const labelPosition = [group.x * scale, -(group.y + 24) * scale, 0]
          drawLabel(
            labelText,
            {
              font: fonts.openSans,
              color: 'black',
              position: labelPosition,
              center_x,
              center_y,
              scale,
              seat_circle_radius,
              scene
            },
            textGeometries,
            materials
          )
          break
        }
        case 'table_2': {
          drawTable(group, groups, { sceneMeshes, ...options }, 2)
          break
        }
        case 'table_circle': {
          drawTable(group, groups, { sceneMeshes, ...options })
          break
        }
        default:
          console.log(group.type)
          break
      }
    }
  }
  const mouseHandlers = raycastMouseEvents({
    camera,
    renderer,
    sceneMeshes,
    events
  })
  const canvasContainer = document.getElementById('seatCanvasContainer')
  if (canvasContainer) {
    canvasContainer.addEventListener('mousemove', mouseHandlers.handleMouseOver)
    canvasContainer.addEventListener('pointerdown', mouseHandlers.handleClick)
  }
}
